import React, { useState } from 'react'
import { useAuth } from '../../auth'
import { canUser } from '../../matching/core/_models'
import { matchingCapabilities } from '../../matching/core/_models'
import { DisputeModal } from '../../matching/routes/Disputes'
import { ReleaseDepositChunkModal } from '../../matching/components/common/ReleaseDepositChunkModal'
import { ResendCallbackButton } from '../../matching/components/common/ResendCallbackButton'
import { useDepositTicketActions } from '../../matching/components/common/useDepositTicketActions'
import { TicketCardHeader } from '../core/response.types'

/** Спор создают только по депозиту, дошедшему до проверки чека, и только если спора ещё нет. */
const FILES_VERIFICATION = 'FILES_VERIFICATION'

/**
 * В статусах проверки чека ручного завершения и отклонения у тикета нет: из них workflow знает
 * только проверку и отклонение самого чека, а `/payment/complete` и `/payment/decline` отвечают
 * 422. Матчинговый список прячет обе кнопки там же (CPAYMENT-1616) — карточка повторяет его
 * правило, а не заводит своё.
 */
const RECEIPT_CHECK_STATUSES = [FILES_VERIFICATION, 'FILES_AUTO_VERIFICATION']

type Props = {
  header: TicketCardHeader
  /** Перезагрузить карточку после операции: её состояние меняется на сервере. */
  onChanged: () => void
}

/**
 * Действия над тикетом на карточке.
 *
 * Права и условия по статусу — те же, что в матчинговых списках, и считаются из того же
 * `matchingCapabilities`. Сами операции зовут общий `useDepositTicketActions` и существующие
 * модалки спора и освобождения депозита: второго пути к операции не заводим.
 */
export const TicketActionsBar: React.FC<Props> = ({ header, onChanged }) => {
  const { currentUser } = useAuth()
  const [showDispute, setShowDispute] = useState(false)
  const [showRelease, setShowRelease] = useState(false)

  const canManage = canUser(currentUser, matchingCapabilities.canEditTickets)
  const canCreateDispute =
    canUser(currentUser, matchingCapabilities.canCreateDispute) &&
    header.status === FILES_VERIFICATION &&
    header.disputeStatus !== 'OPEN'
  const canResend = canUser(currentUser, matchingCapabilities.canResendCallback)

  const { complete, decline } = useDepositTicketActions({
    token: header.token,
    // То же выражение, что в списке: `amount` — фактически оплаченная сумма, до завершения она
    // пуста, и подтверждать завершение по ней значит отправлять ноль.
    amount: header.requisiteAmount ?? header.expectedAmount,
    currentUser: currentUser ?? undefined,
    enabled: canManage,
    onDone: onChanged,
  })

  /** Завершённый тикет не завершают и не отменяют повторно. */
  const isOpen = header.completionDate === null
  const canFinish = canManage && isOpen && !RECEIPT_CHECK_STATUSES.includes(header.status)

  return (
    <>
      <div className='d-flex align-items-center' style={{ gap: 8, flexWrap: 'wrap' }}>
        {canFinish && (
          <button type='button' className='btn btn-sm btn-light-success' onClick={complete}>
            Завершить
          </button>
        )}
        {canFinish && (
          <button type='button' className='btn btn-sm btn-light-danger' onClick={decline}>
            Отменить
          </button>
        )}
        {canCreateDispute && (
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={() => setShowDispute(true)}
          >
            Создать диспут
          </button>
        )}
        {header.canReleaseChunk && (
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={() => setShowRelease(true)}
          >
            Освободить депозит
          </button>
        )}
        {canResend && (
          <ResendCallbackButton
            token={header.token}
            className='btn btn-sm btn-light d-flex align-items-center'
            label='Отправить КБ'
          />
        )}
      </div>

      {showDispute && (
        <DisputeModal
          show={showDispute}
          onClose={() => setShowDispute(false)}
          context={{
            paymentTicketToken: header.token,
            // Спор идёт о сумме, которую клиенту выдали, а не о первоначальном запросе.
            expectedAmount: header.requisiteAmount ?? header.expectedAmount,
            currency: header.currency,
          }}
          onCreated={() => {
            setShowDispute(false)
            onChanged()
          }}
        />
      )}

      {showRelease && (
        <ReleaseDepositChunkModal
          show={showRelease}
          onClose={() => setShowRelease(false)}
          depositToken={header.token}
          heldUntil={header.chunkHeldUntil ?? null}
          onReleased={onChanged}
        />
      )}
    </>
  )
}

export default TicketActionsBar
